import { calculateCostPerInterval, TieredPriceProductId } from "@project/common";
import type {
  BasicUsageParameters,
  FromTo,
  SpecificUsageParameters,
} from "../../components/UsageChartWrapper.js";
import {
  useAssistantUsage,
  useProcessingUsage,
  useStyleJsonUsage,
  useTilesRequestsUsage,
  useStorageUsage,
  useCustomStylesUsage,
  useCustomTilesetsUsage,
  useTeamSeatsUsage,
} from "../../components/hooks/fetchUsages.js";
import { differenceInPartialMonths } from "../../components/utils/dates.js";
import { resolveUsageEventDeltas } from "../../components/utils/usageEventsProcessors.js";
import { useCustomStylesUsageSum, useCustomTilesetUsageSum, useStorageUsageSum, useTeamSeatsUsageSum } from "../../components/hooks/fetchSums.js";
import { useMemo } from "react";

/**
 * Share the free tier allowance evenly over every usage event.
 */
function useAllowanceMultiplier({ from, to }: FromTo, usageEvents: [number, number][]) {
  const months = differenceInPartialMonths(from, to);
  const numberOfUsageEvents = usageEvents.reduce((acc, curr) => curr[1] > 0 ? acc + 1 : acc, 0);
  return numberOfUsageEvents > 0 ? months / numberOfUsageEvents : 0;
}

function useAITokensDailyCost(p: BasicUsageParameters) {
  const {
    assistantUsage,
    isLoadingAssistantUsage,
    isErrorAssistantUsage,
  } = useAssistantUsage(p);

  const inputUsage = assistantUsage?.input ?? [];
  const outputUsage = assistantUsage?.output ?? [];
  const inputAllowanceMultiplier = useAllowanceMultiplier(p, inputUsage);
  const outputAllowanceMultiplier = useAllowanceMultiplier(p, outputUsage);

  return {
    inputTokensDailyCost: calculateCostPerInterval(
      inputUsage,
      TieredPriceProductId.TieredInputToken,
      inputAllowanceMultiplier,
    ),
    outputTokensDailyCost: calculateCostPerInterval(
      outputUsage,
      TieredPriceProductId.TieredOutputToken,
      outputAllowanceMultiplier,
    ),
    isLoadingAssistantUsage,
    isErrorAssistantUsage,
  };
};

function useProcessingDailyCost(p: SpecificUsageParameters) {
  const {
    processingUsage,
    isLoadingProcessingUsage,
    isErrorProcessingUsage,
  } = useProcessingUsage(p);

  const allowanceMultiplier = useAllowanceMultiplier(p, processingUsage);

  return {
    processingDailyCost: calculateCostPerInterval(
      processingUsage,
      TieredPriceProductId.TieredProcessing,
      allowanceMultiplier,
    ),
    isLoadingProcessingUsage,
    isErrorProcessingUsage,
  };
};

function useStyleJsonDailyCost(p: SpecificUsageParameters) {
  const {
    styleJsonUsage,
    isLoadingStyleJsonUsage,
    isErrorStyleJsonUsage,
  } = useStyleJsonUsage(p);

  const allowanceMultiplier = useAllowanceMultiplier(p, styleJsonUsage);

  return {
    styleJsonDailyCost: calculateCostPerInterval(
      styleJsonUsage,
      TieredPriceProductId.TieredMapSession,
      allowanceMultiplier,
    ),
    isLoadingStyleJsonUsage,
    isErrorStyleJsonUsage,
  };
};

function useTilesRequestsDailyCost(p: SpecificUsageParameters) {
  const {
    tilesRequestsUsage,
    isLoadingTilesUsage,
    isErrorTilesUsage,
  } = useTilesRequestsUsage(p);

  const allowanceMultiplier = useAllowanceMultiplier(p, tilesRequestsUsage);

  return {
    tilesRequestsDailyCost: calculateCostPerInterval(
      tilesRequestsUsage,
      TieredPriceProductId.TieredTileUsage,
      allowanceMultiplier,
    ),
    isLoadingTilesUsage,
    isErrorTilesUsage,
  };
};

/**
 * TBD: more robust usage-based pricing model for the following metrics. For now, the way
 * that daily cost is calculated lends itself to units like GB-hours (ignore the
 * negatives; if you consumed this much space for an hour, you're paying for it).
 */

function useStorageDailyCost(p: BasicUsageParameters) {
  const {
    storageUsage,
    isLoadingStorage: isLoadingStorageDeltas,
    isErrorStorage: isErrorStorageDeltas,
  } = useStorageUsage(p);

  const {
    usageSum,
    isLoadingStorage: isLoadingStorageSum,
    isErrorStorage: isErrorStorageSum,
  } = useStorageUsageSum(p);

  const allowanceMultiplier = useAllowanceMultiplier(p, storageUsage);

  return {
    storageDailyCost: calculateCostPerInterval(
      resolveUsageEventDeltas(storageUsage, usageSum),
      TieredPriceProductId.TieredStorage,
      allowanceMultiplier,
    ),
    isLoadingStorage: isLoadingStorageDeltas || isLoadingStorageSum,
    isErrorStorage: isErrorStorageDeltas || isErrorStorageSum,
  };
};

function useCustomStylesDailyCost(p: BasicUsageParameters) {
  const {
    customStylesUsage,
    isLoadingStyles: isLoadingStyleDeltas,
    isErrorStyles: isErrorStyleDeltas,
  } = useCustomStylesUsage(p);

  const allowanceMultiplier = useAllowanceMultiplier(p, customStylesUsage);

  const {
    usageSum,
    isLoadingStyles: isLoadingStylesSum,
    isErrorStyles: isErrorStylesSum,
  } = useCustomStylesUsageSum(p);

  return {
    customStylesDailyCost: calculateCostPerInterval(
      resolveUsageEventDeltas(customStylesUsage, usageSum),
      TieredPriceProductId.TieredCustomStyle,
      allowanceMultiplier,
    ),
    isLoadingStyles: isLoadingStyleDeltas || isLoadingStylesSum,
    isErrorStyles: isErrorStyleDeltas || isErrorStylesSum,
  };
};

function useCustomTilesetsDailyCost(p: BasicUsageParameters) {
  const {
    customTilesetsUsage,
    isLoadingTilesets: isLoadingTilesetsDeltas,
    isErrorTilesets: isErrorTilesetsDeltas,
  } = useCustomTilesetsUsage(p);

  const allowanceMultiplier = useAllowanceMultiplier(p, customTilesetsUsage);

  const {
    usageSum,
    isLoadingTilesets: isLoadingTilesetsSum,
    isErrorTilesets: isErrorTilesetsSum,
  } = useCustomTilesetUsageSum(p);

  return {
    customTilesetsDailyCost: calculateCostPerInterval(
      resolveUsageEventDeltas(customTilesetsUsage, usageSum),
      TieredPriceProductId.TieredCustomTileset,
      allowanceMultiplier,
    ),
    isLoadingTilesets: isLoadingTilesetsDeltas || isLoadingTilesetsSum,
    isErrorTilesets: isErrorTilesetsDeltas || isErrorTilesetsSum,
  };
};

function useTeamSeatsDailyCost(p: BasicUsageParameters) {
  const {
    teamSeatsUsage,
    isLoadingTeamSeats: isLoadingTeamSeatsDeltas,
    isErrorTeamSeats: isErrorTeamSeatsDeltas,
  } = useTeamSeatsUsage(p);

  const allowanceMultiplier = useAllowanceMultiplier(p, teamSeatsUsage);

  const {
    usageSum,
    isLoadingTeamSeats: isLoadingTeamSeatsSum,
    isErrorTeamSeats: isErrorTeamSeatsSum,
  } = useTeamSeatsUsageSum(p);

  return {
    teamSeatsDailyCost: calculateCostPerInterval(
      resolveUsageEventDeltas(teamSeatsUsage, usageSum),
      TieredPriceProductId.TieredAccountUser,
      allowanceMultiplier,
    ),
    isLoadingTeamSeats: isLoadingTeamSeatsDeltas || isLoadingTeamSeatsSum,
    isErrorTeamSeats: isErrorTeamSeatsDeltas || isErrorTeamSeatsSum,
  };
}

export function useAllDailyCosts({ from, to, interval, apiKeyId }: SpecificUsageParameters) {
  const {
    inputTokensDailyCost,
    outputTokensDailyCost,
    isLoadingAssistantUsage,
    isErrorAssistantUsage,
  } = useAITokensDailyCost({ from, to, interval });

  const {
    processingDailyCost,
    isLoadingProcessingUsage,
    isErrorProcessingUsage,
  } = useProcessingDailyCost({ from, to, interval, apiKeyId });

  const {
    styleJsonDailyCost,
    isLoadingStyleJsonUsage,
    isErrorStyleJsonUsage,
  } = useStyleJsonDailyCost({ from, to, interval, apiKeyId });

  const {
    tilesRequestsDailyCost,
    isLoadingTilesUsage,
    isErrorTilesUsage,
  } = useTilesRequestsDailyCost({ from, to, interval, apiKeyId });

  const {
    storageDailyCost,
    isLoadingStorage,
    isErrorStorage,
  } = useStorageDailyCost({ from, to, interval });

  const {
    customStylesDailyCost,
    isLoadingStyles,
    isErrorStyles,
  } = useCustomStylesDailyCost({ from, to, interval });

  const {
    customTilesetsDailyCost,
    isLoadingTilesets,
    isErrorTilesets,
  } = useCustomTilesetsDailyCost({ from, to, interval });

  const {
    teamSeatsDailyCost,
    isLoadingTeamSeats,
    isErrorTeamSeats,
  } = useTeamSeatsDailyCost({ from, to, interval });

  const allDailyCosts: Record<TieredPriceProductId, [number, number][]> = useMemo(() => ({
    [TieredPriceProductId.TieredInputToken]: inputTokensDailyCost,
    [TieredPriceProductId.TieredOutputToken]: outputTokensDailyCost,
    [TieredPriceProductId.TieredProcessing]: processingDailyCost,
    [TieredPriceProductId.TieredTileUsage]: tilesRequestsDailyCost,
    [TieredPriceProductId.TieredStorage]: storageDailyCost,
    [TieredPriceProductId.TieredCustomStyle]: customStylesDailyCost,
    [TieredPriceProductId.TieredCustomTileset]: customTilesetsDailyCost,
    [TieredPriceProductId.TieredMapSession]: styleJsonDailyCost,
    [TieredPriceProductId.TieredAccountUser]: teamSeatsDailyCost,
  } satisfies Record<TieredPriceProductId, [number, number][]>), [
    inputTokensDailyCost,
    outputTokensDailyCost,
    processingDailyCost,
    tilesRequestsDailyCost,
    storageDailyCost,
    customStylesDailyCost,
    customTilesetsDailyCost,
    styleJsonDailyCost,
    teamSeatsDailyCost,
  ]);

  const loadingStates = [
    isLoadingAssistantUsage,
    isLoadingStyles,
    isLoadingTilesets,
    isLoadingStyleJsonUsage,
    isLoadingProcessingUsage,
    isLoadingStorage,
    isLoadingTilesUsage,
    isLoadingTeamSeats,
  ];

  const isLoadingDailyCosts = loadingStates.some(Boolean);

  const errorStates = [
    isErrorAssistantUsage,
    isErrorStyleJsonUsage,
    isErrorProcessingUsage,
    isErrorTilesUsage,
    isErrorStyles,
    isErrorTilesets,
    isErrorStorage,
    isErrorTeamSeats,
  ];

  const isErrorDailyCosts = errorStates.some(Boolean);

  return {
    allDailyCosts,
    isLoadingDailyCosts,
    isErrorDailyCosts,
  };
};
